// src/api/client.js
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:4000/api',
});

// ⛳ Interceptor REQUEST: agrega Authorization si no está
api.interceptors.request.use((config) => {
  const t = localStorage.getItem('token');
  if (t) {
    if (!config.headers) config.headers = {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// ⛳ Interceptor RESPONSE: refresh automático cuando el access token expira
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Sesión revocada (salió en otro lado, un admin la cerró o lo dieron de
    // baja): no hay nada que renovar, al login.
    if (error.response?.status === 401 &&
        ['SESSION_CLOSED', 'USUARIO_INACTIVO'].includes(error.response?.data?.code)) {
      forceLogout();
      return Promise.reject(error);
    }

    // Solo interceptar 401 con TOKEN_EXPIRED y que no sea el propio refresh
    if (
      error.response?.status === 401 &&
      error.response?.data?.code === 'TOKEN_EXPIRED' &&
      !originalRequest._retry
    ) {
      // Si ya hay un refresh en curso, encolar esta request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // No hay refresh token, forzar logout
        isRefreshing = false;
        processQueue(error);
        forceLogout();
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(
          `${api.defaults.baseURL}/auth/refresh`,
          { refreshToken },
          { withCredentials: true }   // renueva la cookie de /api/uploads
        );

        // Una respuesta sin token (p. ej. la página anti-bots del hosting) es
        // una falla pasajera, no una sesión inválida.
        if (!data?.token) throw new Error('Refresh sin token');

        // Guardar nuevo access token
        localStorage.setItem('token', data.token);
        if (data.user) {
          localStorage.setItem('usuario', JSON.stringify(data.user));
        }

        // Notificar al AuthContext si hay listener registrado
        if (api._onTokenRefreshed) {
          api._onTokenRefreshed(data.token, data.user);
        }

        // Reintentar requests en cola con el nuevo token
        processQueue(null, data.token);

        // Reintentar la request original
        originalRequest.headers.Authorization = `Bearer ${data.token}`;
        return api(originalRequest);

      } catch (refreshError) {
        processQueue(refreshError);
        // Solo se cierra la sesión si el backend rechazó el refresh token.
        // Red caída, 5xx o 429 (servidor despertando, señal mala en obra): se
        // falla este pedido y el próximo vuelve a intentar renovar.
        const status = refreshError.response?.status;
        if (status === 400 || status === 401) forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Forzar logout y redirigir al login
function forceLogout() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('usuario');
  // Redirigir al login (funciona con HashRouter y BrowserRouter)
  window.location.href = window.location.origin + '/#/login';
}

export default api;
